
import React from 'react';
import { 
  ArrowLeft, 
  Building2, 
  Briefcase, 
  Scale, 
  TrendingUp, 
  Zap, 
  ShieldCheck, 
  Globe, 
  ArrowRight,
  Database,
  Users
} from 'lucide-react';

import { Language, translations } from '../../translations';

export const UseCasesView: React.FC<{ onBack: () => void, language: Language }> = ({ onBack, language }) => {
  const t = translations[language];
  const useCases = [
    {
      title: t.useCases.banks,
      icon: <Building2 className="text-blue-500" />,
      content: t.useCases.banksDesc
    },
    {
      title: t.useCases.pevc,
      icon: <Briefcase className="text-indigo-500" />,
      content: t.useCases.pevcDesc
    },
    {
      title: t.useCases.law,
      icon: <Scale className="text-emerald-500" />,
      content: t.useCases.lawDesc
    },
    {
      title: t.useCases.stocks,
      icon: <TrendingUp className="text-amber-500" />,
      content: t.useCases.stocksDesc
    },
    {
      title: t.useCases.fintechDefi,
      icon: <Zap className="text-violet-500" />,
      content: t.useCases.fintechDefiDesc
    },
    {
      title: t.useCases.familyOffices,
      icon: <Users className="text-rose-500" />,
      content: t.useCases.familyOfficesDesc
    },
    {
      title: t.useCases.riskIntel,
      icon: <Globe className="text-blue-600" />,
      content: t.useCases.riskIntelDesc
    }
  ];

  return (
    <div className="min-h-screen bg-white dark:bg-slate-950 transition-colors duration-500 pb-32">
      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 bg-white/80 dark:bg-slate-950/80 backdrop-blur-xl border-b border-slate-100 dark:border-slate-900">
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
          <button onClick={onBack} className="flex items-center space-x-2 text-slate-500 dark:text-slate-400 font-black text-xs uppercase tracking-widest hover:text-blue-600 dark:hover:text-blue-400 transition-all group">
            <div className="p-2 bg-slate-100 dark:bg-slate-900 rounded-lg group-hover:bg-blue-600 group-hover:text-white transition-all"><ArrowLeft size={16} /></div>
            <span>{t.useCases.returnHome}</span>
          </button>
          <div className="flex items-center space-x-3">
            <img src="assets/logo.png" alt="Minerva Logo" className="w-8 h-8 object-contain" />
            <span className="text-lg font-black tracking-tighter dark:text-white uppercase italic">{t.useCases.titlePage}</span>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <header className="pt-48 pb-24 px-6 relative overflow-hidden">
        <div className="max-w-5xl mx-auto text-center space-y-10 relative z-10">
          <div className="inline-flex items-center space-x-3 px-4 py-2 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-full text-[10px] font-black uppercase border border-blue-100 dark:border-blue-800/50">
            <ShieldCheck size={14} />
            <span>{t.useCases.institutionalApps}</span>
          </div>
          <h1 className="text-6xl md:text-8xl font-black text-slate-900 dark:text-white tracking-tighter italic leading-none">
            {t.useCases.industrySolutions}
          </h1>
          <p className="text-xl text-slate-500 dark:text-slate-400 font-medium leading-relaxed max-w-3xl mx-auto">
            {t.useCases.solutionsDesc}
          </p>
        </div>
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full opacity-5 pointer-events-none">
           <Database size={600} className="mx-auto" />
        </div>
      </header>

      {/* Use Cases Grid */}
      <main className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {useCases.map((uc, index) => (
            <div key={index} className="p-10 bg-slate-50 dark:bg-slate-900 rounded-[3rem] border border-slate-100 dark:border-slate-800 shadow-sm group hover:bg-white dark:hover:bg-slate-950 hover:shadow-xl transition-all flex flex-col">
              <div className="p-5 bg-white dark:bg-slate-800 rounded-3xl w-fit mb-8 shadow-sm group-hover:bg-blue-600 group-hover:text-white transition-all">
                {uc.icon}
              </div>
              <h3 className="text-2xl font-black text-slate-900 dark:text-white mb-6 uppercase italic tracking-tighter leading-tight">
                {uc.title}
              </h3>
              <p className="text-slate-500 dark:text-slate-400 font-medium leading-relaxed flex-1">
                {uc.content}
              </p>
            </div>
          ))}
        </div>

        {/* Call to Action */}
        <section className="mt-40 bg-slate-900 dark:bg-blue-600 rounded-[4rem] p-12 md:p-24 text-center text-white relative overflow-hidden">
          <div className="relative z-10 space-y-10">
            <h2 className="text-4xl md:text-6xl font-black italic tracking-tighter">{t.useCases.ready}</h2>
            <p className="text-blue-200 dark:text-white/80 text-xl max-w-2xl mx-auto font-medium">
              {t.useCases.readyDesc}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
              <button 
                onClick={onBack}
                className="bg-white text-slate-900 px-12 py-6 rounded-[2rem] font-black uppercase text-xs tracking-widest hover:bg-blue-50 transition-all shadow-2xl active:scale-95 flex items-center space-x-3"
              >
                <span>{t.useCases.init}</span>
                <ArrowRight size={18} />
              </button>
            </div>
          </div>
          <div className="absolute top-0 left-0 w-full h-full opacity-10 pointer-events-none">
            <Globe size={800} className="absolute -top-40 -left-40" />
          </div>
        </section>
      </main>

      <footer className="mt-40 py-12 text-center text-[10px] font-black text-slate-300 dark:text-slate-800 uppercase tracking-widest">
         {t.about.rights}
      </footer>
    </div>
  );
};
